import React, { useEffect, useMemo, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Loader2, Code2, SlidersHorizontal, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { FeatureFlag, FeatureFlagConditions, FeatureFlagEntry } from '@/client/types/feature-flags';
import { useUpdateFeatureFlagMutation } from '@/client/api/feature-flags/feature-flags.mutation';
import ConditionsBuilder from './ConditionsBuilder';
import { isConditionsEmpty, featureFlagPermissionError } from './utils';

interface FeatureFlagEditDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** The flag being edited, or `null` to create a brand-new override. */
  flag: FeatureFlag | null;
}

// ─── Small inline toggle ───────────────────────────────────────────────────────

const Toggle: React.FC<{ checked: boolean; onChange: (v: boolean) => void; label?: string }> = ({
  checked,
  onChange,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
      checked ? 'bg-green-500' : 'bg-gray-300'
    }`}
  >
    <span
      className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
        checked ? 'translate-x-5' : 'translate-x-0.5'
      }`}
    />
  </button>
);

const NAME_RE = /^[a-z0-9]+(\.[a-z0-9_]+)*$/i;

const FeatureFlagEditDrawer: React.FC<FeatureFlagEditDrawerProps> = ({ isOpen, onClose, flag }) => {
  const isCreate = !flag;
  const updateMutation = useUpdateFeatureFlagMutation();

  const [name, setName] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [description, setDescription] = useState('');
  const [conditions, setConditions] = useState<FeatureFlagConditions>({});
  const [advanced, setAdvanced] = useState(false);
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // (Re)initialise whenever the drawer opens or the target flag changes.
  useEffect(() => {
    if (!isOpen) return;
    setName(flag?.name ?? '');
    setEnabled(flag?.enabled ?? true);
    setDescription(flag?.description ?? '');
    setConditions((flag?.conditions ?? {}) as FeatureFlagConditions);
    setAdvanced(false);
    setJsonError(null);
  }, [isOpen, flag]);

  // Keep the JSON escape-hatch in sync with the structured fields on entry.
  const structuredEntry = useMemo<FeatureFlagEntry>(() => {
    const normalized = isConditionsEmpty(conditions) ? null : conditions;
    return { enabled, description, conditions: normalized };
  }, [enabled, description, conditions]);

  const enterAdvanced = () => {
    setJsonText(JSON.stringify(structuredEntry, null, 2));
    setJsonError(null);
    setAdvanced(true);
  };

  const exitAdvanced = () => {
    // Try to fold JSON edits back into the structured fields.
    try {
      const parsed = JSON.parse(jsonText) as FeatureFlagEntry;
      setEnabled(!!parsed.enabled);
      setDescription(parsed.description ?? '');
      setConditions((parsed.conditions ?? {}) as FeatureFlagConditions);
      setJsonError(null);
      setAdvanced(false);
    } catch (e: any) {
      setJsonError(`Invalid JSON: ${e.message}`);
    }
  };

  const buildPayload = (): { name: string; entry: FeatureFlagEntry } | null => {
    const targetName = (isCreate ? name : flag!.name).trim();
    if (!targetName) {
      toast.error('A flag name is required.');
      return null;
    }
    if (isCreate && !NAME_RE.test(targetName)) {
      toast.error('Flag name must be dot-pathed, e.g. "asset.upload.video".');
      return null;
    }

    let entry: FeatureFlagEntry;
    if (advanced) {
      try {
        entry = JSON.parse(jsonText) as FeatureFlagEntry;
      } catch (e: any) {
        setJsonError(`Invalid JSON: ${e.message}`);
        return null;
      }
      if (typeof entry.enabled !== 'boolean') {
        setJsonError('The entry must include a boolean "enabled" field.');
        return null;
      }
    } else {
      entry = structuredEntry;
    }
    return { name: targetName, entry };
  };

  const handleSave = () => {
    const payload = buildPayload();
    if (!payload) return;

    updateMutation.mutate(payload, {
      onSuccess: (res) => {
        toast.success(
          `Saved “${res.feature}”${res.version != null ? ` · v${res.version}` : ''}`,
          { description: `Propagation: ${res.propagation}` },
        );
        onClose();
      },
      onError: (err: any) =>
        toast.error(
          featureFlagPermissionError(err) ?? err?.message ?? 'Failed to save the feature flag.',
        ),
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-xl"
      >
        <SheetHeader className="border-b border-gray-100 px-6 py-4 text-left">
          <SheetTitle className="text-lg font-semibold">
            {isCreate ? 'New feature flag override' : 'Edit feature flag'}
          </SheetTitle>
          <SheetDescription className="text-sm text-gray-500">
            {isCreate ? (
              'Create a live Mongo override for a flag.'
            ) : (
              <span className="font-mono text-xs text-gray-600">{flag!.name}</span>
            )}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Full-replace warning */}
          <div className="mb-5 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <p>
              Saving writes the <strong>entire</strong> entry as an override — enabled,
              description and conditions together. Partial edits are not possible; every field
              below is persisted.
            </p>
          </div>

          {isCreate && (
            <div className="mb-5">
              <label className="text-sm font-medium text-gray-700">Flag name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="asset.upload.video"
                className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-sm outline-none focus:border-gray-400"
              />
              <p className="mt-1 text-xs text-gray-400">
                Dot-pathed with any-level veto — a <code>false</code> ancestor kills all
                descendants.
              </p>
            </div>
          )}

          {/* Enabled */}
          <div className="mb-5 flex items-center justify-between rounded-lg border border-gray-200 p-3">
            <div>
              <p className="text-sm font-medium text-gray-800">Enabled</p>
              <p className="text-xs text-gray-500">
                Statically resolved value (before conditions are applied).
              </p>
            </div>
            <Toggle checked={enabled} onChange={setEnabled} />
          </div>

          {/* Mode switch */}
          <div className="mb-4 flex items-center justify-end">
            {advanced ? (
              <button
                type="button"
                onClick={exitAdvanced}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900"
              >
                <SlidersHorizontal className="h-3.5 w-3.5" /> Structured editor
              </button>
            ) : (
              <button
                type="button"
                onClick={enterAdvanced}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-gray-900"
              >
                <Code2 className="h-3.5 w-3.5" /> Advanced (JSON)
              </button>
            )}
          </div>

          {advanced ? (
            <div>
              <textarea
                value={jsonText}
                onChange={(e) => {
                  setJsonText(e.target.value);
                  setJsonError(null);
                }}
                spellCheck={false}
                rows={16}
                className="w-full rounded-lg border border-gray-200 bg-gray-900 p-3 font-mono text-xs text-gray-100 outline-none focus:border-gray-500"
              />
              {jsonError && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                  <AlertTriangle className="h-3.5 w-3.5" /> {jsonError}
                </p>
              )}
              <p className="mt-2 flex items-center gap-1.5 text-xs text-gray-400">
                <Info className="h-3.5 w-3.5" /> The full entry sent to the API. Must include a
                boolean <code>enabled</code>.
              </p>
            </div>
          ) : (
            <>
              <div className="mb-5">
                <label className="text-sm font-medium text-gray-700">Description</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  placeholder="What does this flag control?"
                  className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-gray-400"
                />
              </div>

              <div className="mb-1">
                <h3 className="text-sm font-semibold text-gray-800">Conditions</h3>
                <p className="mb-3 text-xs text-gray-500">
                  Who actually reaches the flag at runtime. Leave empty for &ldquo;all users&rdquo;.
                </p>
              </div>
              <ConditionsBuilder value={conditions} onChange={setConditions} />
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-6 py-4">
          <Button variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isCreate ? 'Create override' : 'Save override'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FeatureFlagEditDrawer;
